/**
 * Web & app chat client (R7) — the customer's side of the restaurant's Team
 * Inbox, through the online-ordering BFF.
 *
 *   GET  /v1/online-ordering/chat/availability  -> {enabled, reason}
 *   GET  /v1/online-ordering/chat/thread        -> {conversationId, messages, nextCursor}
 *   POST /v1/online-ordering/chat/messages      -> the created message
 *   POST /v1/online-ordering/chat/uploads       -> {uploadUrl, gcsPath}
 *
 * Availability is a TENANT question ("is this restaurant's chat on?"), so it
 * takes no signed-in customer and is safe to call at boot. Everything else
 * is the customer's own thread and needs their token.
 *
 * The app never learns WHY chat is off — only that it is. Which plan a
 * restaurant is on is not a customer's business.
 */
import { apiFetch } from "./http";

export interface ChatAvailability {
  enabled: boolean;
  reason?: string | null;
  /** Whether anyone is at the restaurant's inbox right now. One bit by
   * design — never a count and never a name. Unlike `enabled`, this changes
   * minute to minute, so it must never be read from the session cache. */
  agentsOnline?: boolean;
}

export type ChatAuthor = "customer" | "restaurant";

export interface ChatMessage {
  id: string;
  author: ChatAuthor;
  type: "text" | "image" | "location";
  text?: string | null;
  /** Short-lived signed URL — never a storage path, and not worth caching. */
  imageUrl?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  timestamp: string;
}

export interface ChatThread {
  conversationId: string;
  messages: ChatMessage[];
  unreadFromRestaurant: number;
  nextCursor?: string | null;
  /** An agent is composing right now. A heartbeat with a short server-side
   *  TTL, so a closed agent tab stops it on its own. */
  restaurantTyping?: boolean;
  /** When an agent last opened this thread. The customer's own messages
   *  sent before this are marked read. Carries no agent identity. */
  restaurantLastReadAt?: string | null;
}

export interface ChatSendInput {
  type: "text" | "image" | "location";
  text?: string;
  gcsPath?: string;
  mimeType?: string;
  latitude?: number;
  longitude?: number;
  /** Idempotency key — a phone on a flaky connection retries, and without
   *  this the restaurant answers the same sentence twice. */
  clientMessageId?: string;
}

export interface ChatUploadTarget {
  uploadUrl: string;
  gcsPath: string;
  expiresInSeconds: number;
}

export const chatApi = {
  availability(): Promise<ChatAvailability> {
    return apiFetch<ChatAvailability>("/v1/online-ordering/chat/availability", {
      method: "GET",
      skipAuth: true,
    });
  },

  /** Fire-and-forget: a dropped heartbeat is not worth surfacing, and the
   *  indicator expires by itself a few seconds later. */
  typing(): Promise<void> {
    return apiFetch<void>("/v1/online-ordering/chat/typing", { method: "POST" }).catch(
      () => undefined,
    );
  },

  thread(limit = 50, cursor?: string): Promise<ChatThread> {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (cursor) qs.set("cursor", cursor);
    return apiFetch<ChatThread>(`/v1/online-ordering/chat/thread?${qs}`, { method: "GET" });
  },

  send(input: ChatSendInput): Promise<ChatMessage> {
    return apiFetch<ChatMessage>("/v1/online-ordering/chat/messages", {
      method: "POST",
      body: input,
    });
  },

  uploadTarget(contentType: string): Promise<ChatUploadTarget> {
    const qs = new URLSearchParams({ contentType });
    return apiFetch<ChatUploadTarget>(`/v1/online-ordering/chat/uploads?${qs}`, {
      method: "POST",
    });
  },

  /**
   * PUT the bytes straight to storage. Deliberately NOT through `apiFetch`:
   * this is a signed GCS URL, and attaching our Authorization / X-API-KEY
   * headers to it would break the signature.
   */
  async upload(target: ChatUploadTarget, file: File): Promise<void> {
    const res = await fetch(target.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  },
};
